import type { Task } from '../../lib/api';

export type StatusValue = Task['status'];

// ── Task grouping (multi-assignee tasks created as one logical unit) ───────────

export interface TaskGroup {
  key: string;
  tasks: Task[];
  representative: Task; // first task — used for title, context, dates
}

export const STATUS_ORDER: StatusValue[] = ['open', 'in_progress', 'done'];

export function groupTasks(tasks: Task[]): TaskGroup[] {
  const map = new Map<string, Task[]>();
  for (const t of tasks) {
    // Tasks created for multiple assignees share the same second timestamp
    const key = `${t.title}::${t.created_by}::${t.thread_id ?? ''}::${t.created_at.slice(0, 19)}`;
    const existing = map.get(key);
    if (existing) { existing.push(t); } else { map.set(key, [t]); }
  }
  return Array.from(map.entries()).map(([key, tasks]) => ({
    key,
    tasks,
    representative: tasks[0],
  }));
}

// A group's "effective status" is the current user's task status within it,
// or the lowest-progress status in the group otherwise.
export function groupStatus(g: TaskGroup, currentUserId?: string): StatusValue {
  const mine = g.tasks.find((t) => t.assigned_to === currentUserId);
  if (mine) return mine.status;
  return g.tasks.reduce<StatusValue>((worst, t) => (
    STATUS_ORDER.indexOf(t.status) < STATUS_ORDER.indexOf(worst) ? t.status : worst
  ), 'done');
}

export function statusNext(s: StatusValue): StatusValue {
  if (s === 'open') return 'in_progress';
  if (s === 'in_progress') return 'done';
  return 'open';
}

export function statusLabel(s: StatusValue) {
  if (s === 'open') return 'Open';
  if (s === 'in_progress') return 'In Progress';
  return 'Done';
}
